import {h, Component, ComponentChild} from 'preact'
import qs from 'qs'
import axios, {AxiosError, AxiosResponse} from 'axios'
import globals from '../../utils/globals'
import Header from './Header'
import Loader from '../ui-components/Loader'
import OverviewFilters from './filters/OverviewFilters'
import OverviewColumns, {copyColumns} from './columns/OverviewColumns'
import Table from '../table/Table'
import EmptyPage from '../empty-page/EmptyPage'
import AlertComponent from '../ui-components/AlertComponent'
import copyToClipboard from '../utils/copyToClipboard'
import tracker from '../../components/tracker/publish'
import {Model} from '../table/types'
import Icon from '../assets'
import ButtonControl from '../button-control'
import {CustomButton} from '../header'
import { csrfToken } from '../utils/csrfToken'
import CustomViewActions from './custom-views/CustomViewActions'

export type Filters = {
  [filter: string]: Array<any> | string | any
}

export type BulkAction = {
  onClick: (data: []) => Promise<any>,
  successMessage: string
}

export type ViewColumn = {
  accessor: string,
  isHidden: boolean
}

export type FilterSelector = {
  id: string,
  type: string,
  name?: string,
  component?: Function,
  items?: Array<any>,
  isSearchable?: boolean,
  fetchUrl?: string,
  isMain?: boolean,
}

type Sort = {
  [key: string]: string,
}

type Props = {
  type: string,
  headerTitle: string,
  headerButtons?: Array<CustomButton | ComponentChild>,
  withFilters?: Array<FilterSelector>,
  withSearch?: boolean,
  searchPlaceholder?: string,
  withSorting?: Sort,
  withPagination?: {
    page: number,
    pageSize: number
  },
  withColumns?: boolean,
  rowId: string,
  endpoint: string,
  dataKey: string,
  model: Model[],
  EmptyStateComponent: JSX.Element,
  bulkActions?: any[],
  isBeta?: boolean,
  withCustomViews?: string
}

type State = {
  data: [],
  total?: string,
  loading: boolean,
  areFiltersApplied: boolean,
  filters?: Filters,
  pagination?: {
    page: number,
    pageSize: number
  },
  sort?: Sort,
  columns: Model[],
  [key: string]: any,
  customViewTitle: string,
  customViewError: string,
  showAlert: boolean,
  alertMessage: string,
  alertType: string|null,
  alertVisibleFor: number|null,
  customViewId?: number,
  selectedRows: number[]
}

class Overview extends Component<Props, State> {
  private STORAGE_KEY = `${this.props.type}-filter-criteria`
  private defaultCdn = globals('defaultCdn')
  private overviewPath = `${this.props.withCustomViews === 'default' ? '' : `/${this.props.withCustomViews}`}/overview`

  constructor (props: Props) {
    super(props)
    this.state = {
      data: [],
      total: undefined,
      loading: true,
      areFiltersApplied: false,
      ...this.getDefaultFilteringOptions(),
      customViewTitle: '',
      customViewError: '',
      showAlert: false,
      alertMessage: '',
      alertType: null,
      alertVisibleFor: null,
      columns: copyColumns(props.model),
      selectedRows: []
    }

    this.handleResponse = this.handleResponse.bind(this)
    this.onPageChange = this.onPageChange.bind(this)
    this.onPageSizeChange = this.onPageSizeChange.bind(this)
    this.onFilterChange = this.onFilterChange.bind(this)
    this.onClearFilterCriteria = this.onClearFilterCriteria.bind(this)
    this.onShareView = this.onShareView.bind(this)
    this.onSortChange = this.onSortChange.bind(this)
    this.onSaveCustomView = this.onSaveCustomView.bind(this)
    this.onRefresh = this.onRefresh.bind(this)
    this.handleCustomViewCreateError = this.handleCustomViewCreateError.bind(this)
    this.handleCustomViewCreateSuccess = this.handleCustomViewCreateSuccess.bind(this)
    this.handleCustomViewUpdateSuccess = this.handleCustomViewUpdateSuccess.bind(this)
    this.applySelectedColumns = this.applySelectedColumns.bind(this)
    this.onSelectRow = this.onSelectRow.bind(this)
    this.onSelectAll = this.onSelectAll.bind(this)
    this.performBulkAction = this.performBulkAction.bind(this)
    this.onDeleteCustomView = this.onDeleteCustomView.bind(this)
  }

  getDefaultFilteringOptions () : { [field: string] : {}} {
    const { withFilters, withSearch, withSorting, withPagination } = this.props
    const filteringOptions = {}

    if (withFilters) {
      const filterSelectors = withFilters
      const emptyFilters = {}
      filterSelectors.forEach((selector: FilterSelector) => {
        if(selector.id === 'separator') return
        const payload = selector.type === 'radio' ? null : []
        Object.assign(emptyFilters, { [selector.id]: payload })
      })

      if(withSearch) {
        Object.assign(emptyFilters, { search: '' })
      }

      Object.assign(filteringOptions, { filters: emptyFilters })
    }

    if (withSorting) {
      Object.assign(filteringOptions, { sort: withSorting })
    }

    if (withPagination) {
      const { page, pageSize } = withPagination
      Object.assign(filteringOptions, { pagination: { page, pageSize } })
    }

    return filteringOptions
  }

  applyViewFromStorage () {
    if (!window.sessionStorage.getItem(this.STORAGE_KEY)) {
      window.sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.getDefaultFilteringOptions()))
    } else {
      const criteria = JSON.parse(window.sessionStorage.getItem(this.STORAGE_KEY) as string)
      const areFiltersApplied = JSON.stringify(criteria.filters) !== JSON.stringify(this.getDefaultFilteringOptions().filters)
      this.setState({
        filters: criteria.filters,
        pagination: criteria.pagination,
        sort: criteria.sort,
        ...{ areFiltersApplied }
      })
      if (criteria.columns) this.setState({ columns: this.getColumnsFromView(criteria.columns) })
    }
  }

  static getCustomViewId () {
    return parseInt(window.location.pathname.split('/overview/')[1])
  }

  static getSharedView () {
    const search = qs.parse(window.location.search, { ignoreQueryPrefix: true })
    const view = search?.view

    try {
      return JSON.parse(window.atob(view))
    } catch (e) {
      return null
    }
  }

  static isCustomView (): boolean {
    return !!Overview.getCustomViewId()
  }

  static isSharedView (): boolean {
    return !!Overview.getSharedView()
  }

  async getView () {
    const customViewId = Overview.getCustomViewId()
    if (customViewId) {
      await this.fetchCustomView(customViewId)
      return
    }

    const sharedView = Overview.getSharedView()
    if (sharedView) {
      this.setSharedView(sharedView)
      return
    }

    this.applyViewFromStorage()
  }

  async componentDidMount () {
    await this.getView()
    await this.fetch()
  }

  async fetchCustomView (viewId: number) {
    await axios.get(`/view/${viewId}`)
      .then(res => this.setCustomView(res, viewId))
      .catch(() => {})
  }

  async fetch () {
    this.setState({ loading: true, selectedRows: [] })
    const { endpoint } = this.props
    const { pagination, sort, filters } = this.state

    const dynamicFields = {}

    if (filters?.assignedModerator) {
      Object.assign(dynamicFields, { assignedModerator: filters?.assignedModerator.map((filter: { value: string }) => filter.value) })
    }

    if (filters?.author) {
      Object.assign(dynamicFields, { author: filters?.author.map((filter: { value: string }) => filter.value) })
    }

    if (filters?.userTag) {
      Object.assign(dynamicFields, { userTag: filters?.userTag.map((filter: { value: string }) => filter.value) })
    }

    if (filters?.productArea) {
      Object.assign(dynamicFields, { productArea: filters?.productArea.map((filter: { id: string }) => filter.id) })
    }

    await axios.post(endpoint, {
      pagination,
      sort,
      filters: {...filters, ...dynamicFields}
    })
      .then(res => this.handleResponse(res))
      .catch(() => this.setState({ loading: false }))
  }

  async createCustomView (name: string) {
    const { filters, sort, columns } = this.state
    const data = {
      name,
      criteria: {
        filters: filters,
        sort: sort,
        columns: this.getColumnsForView(columns)
      },
      type: this.props.withCustomViews
    }

    await axios.put('/view', data)
      .then(this.handleCustomViewCreateSuccess)
      .catch(this.handleCustomViewCreateError)
  }

  async updateCustomView () {
    const { customViewId, customViewTitle, filters, sort, columns } = this.state
    const data = {
      name: customViewTitle,
      criteria: {
        filters: filters,
        sort: sort,
        columns: this.getColumnsForView(columns)
      },
      type: this.props.withCustomViews
    }

    await axios.post(`/view/${customViewId}`, data)
      .then(this.handleCustomViewUpdateSuccess)
      .catch(() => this.showSomethingWentWrong())
  }

  onPageChange (page: number) {
    const pageSize = this.state.pagination?.pageSize
    this.updateFilterCriteria('pagination', { page, pageSize })
  }

  onPageSizeChange (pageSize: number) {
    const page = this.props.withPagination?.page
    this.updateFilterCriteria('pagination', { page, pageSize })
  }

  onSortChange (order: string) {
    const { sort } = this.state

    const currentOrder = sort && Object.keys(sort).filter(currentOrder => currentOrder === order)

    if(currentOrder && currentOrder.length === 0) {
      this.setState({ sort: { [order]: 'asc' }})
    }

    const direction = this.state.sort?.[order]

    if (direction === 'desc') this.updateFilterCriteria('sort', { [order]: 'asc' })
    if (direction === 'asc') this.updateFilterCriteria('sort', { [order]: 'desc' })
  }

  onFilterChange (filter: string, payload: Array<string> | string | null) {
    const { filters } = this.state
    this.updateFilterCriteria('filters', {...filters, ...{ [filter]: payload }})
    // always to to the first page when updating filters
    const pagination = { page: 1, pageSize: this.state.pagination!.pageSize }
    this.setState({ areFiltersApplied: true, pagination })
  }

  onClearFilterCriteria () {
    if (Overview.isCustomView() || Overview.isSharedView()) {
      this.setState({ ...this.getDefaultFilteringOptions(), areFiltersApplied: false }, () => {
        this.fetch()
      })
    } else {
      this.setState({ ...this.getDefaultFilteringOptions(), areFiltersApplied: false }, () => {
        this.fetch().then(() => {
          window.sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.getDefaultFilteringOptions()))
        })
      })
    }
  }
  onRefresh() {
    this.onPageChange(1)
  }

  onShareView () {
    const sharableViewUrl = this.getSharableViewUrl()

    copyToClipboard(sharableViewUrl)

    this.setState({
      showAlert: true,
      alertMessage: 'Link copied to clipboard!',
      alertType: 'success',
      alertVisibleFor: 3000
    })

    this.trackCustomViewShared()
  }

  async onDeleteCustomView () {
    const { customViewId } = this.state
    await axios.delete(`/view/${customViewId}`, { data: { csrfToken: csrfToken() }} )
      .then(() => window.location.assign(this.overviewPath))
      .catch(() => {
        this.setState({ showDeleteCustomViewModal: false })
        this.showSomethingWentWrong()
      })
  }

  async onSaveCustomView (newCustomViewName?: string) {
    if (newCustomViewName != undefined) {
      if (newCustomViewName === '') {
        this.setState({ customViewError: 'Please enter a name for your custom view' })
        return
      }
      if (newCustomViewName.length > 250) {
        this.setState({ customViewError:
            'Maximum length: 250 characters. Please reduce the length of the name of your custom view' })
        return
      }
      await this.createCustomView(newCustomViewName)
    } else {
      this.validateCustomViewTitleName()
      if (this.state.alertType === 'error') {
        return
      }
      await this.updateCustomView()
    }
  }

  updateFilterCriteria (key: string, value: {}) {
    if (Overview.isCustomView() || Overview.isSharedView()) {
      this.setState({ [key]: value }, () => {
        this.fetch()
      })
    } else {
      let storage = JSON.parse(window.sessionStorage.getItem(this.STORAGE_KEY) as string)
      storage[key] = value
      window.sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(storage))
      this.setState({ [key]: storage[key] }, () => {
        this.fetch()
      })
    }
  }

  handleResponse (response: AxiosResponse) {
    const { total } = response.data
    const data = response.data[this.props.dataKey]

    this.setState({
      data: data || [],
      total: total || undefined,
      loading: false
    })
  }

  handleCustomViewCreateSuccess (response: AxiosResponse) {
    const { data } = response

    if (data === undefined) {
      return
    }

    this.redirectToCustomView(data.viewId)
  }

  handleCustomViewUpdateSuccess (response: AxiosResponse) {
    this.redirectToCustomView(this.state.customViewId as number)
  }

  redirectToCustomView (viewId: number) {
    window.location.assign(`${this.overviewPath}/${viewId}?showMessage=success`)
  }

  handleCustomViewCreateError (error: AxiosError) {
    if (error.response && error.response.status === 422) {
      this.setState({ customViewError: 'This custom view name already exists' })
      return
    }

    this.showSomethingWentWrong()
  }

  showSomethingWentWrong () {
    this.setState({
      showAlert: true,
      alertMessage: 'Something went wrong. Please try again',
      alertType: 'error',
      alertVisibleFor: 3000,
      loading: false
    })
  }

  getColumnsFromView(viewColumns: ViewColumn[]) {
    return this.state.columns.map((column) => {
      let viewColumn = viewColumns.find((viewColumn: ViewColumn) => column.accessor === viewColumn.accessor)
      if (viewColumn) column.isHidden = !!viewColumn.isHidden
      return column
    })
  }

  getColumnsForView(columns: Model[]) {
    return columns.map((column) => {
      return { accessor: column.accessor, isHidden: !!column.isHidden }
    })
  }

  setCustomView (response: AxiosResponse, customViewId: number) {
    if (response.data.length === 0) {
      return
    }

    const { name, criteria } = response.data
    const { filters, sort, columns } = criteria

    this.setState({
      customViewId,
      filters: {...this.state.filters, ...filters },
      sort,
      areFiltersApplied: true,
      customViewTitle: name
    })
    columns && this.setState({ columns: this.getColumnsFromView(columns) })

    if (window.location.search.includes('?showMessage=success')) {
      this.setState({
        showAlert: true,
        alertMessage: `Custom view ‘${name}’ saved successfully!`,
        alertType: 'success',
        alertVisibleFor: 3000
      })
      window.history.replaceState(null, '', customViewId.toString())
      this.trackCustomViewSaved()
    } else {
      this.trackCustomViewOpened()
    }
  }

  setSharedView (sharedView: {filters: Filters, sort: Sort, columns: ViewColumn[]}) {
    const { filters, sort, columns } = sharedView

    this.setState({
      filters: filters,
      sort: sort
    })
    columns && this.setState({ columns: this.getColumnsFromView(columns) })
  }

  getViewParameters (): string {
    const { filters, sort, columns } = this.state

    return JSON.stringify({
      filters: filters,
      sort: sort,
      columns: this.getColumnsForView(columns)
    })
  }

  getSharableViewUrl (): string {
    return `${window.location.origin}${this.overviewPath}?view=${window.btoa(this.getViewParameters())}`
  }

  getPaginationProps () {
    const { withPagination } = this.props
    if(!withPagination) return

    const { pagination, total } = this.state

    return {
      pagination: Object.assign({},  pagination, { total }),
      onPageChange: this.onPageChange,
      onPageSizeChange: this.onPageSizeChange
    }
  }

  getTableProps () {
    const { withPagination, withSorting, withColumns, model, rowId, dataKey, bulkActions } = this.props
    const { data, columns, selectedRows } = this.state

    const tableProps = {
      model: columns,
      rows: data,
      rowId,
      firstColumnSticky: true,
      actionElements: <div className='overview_table-action-elements'>
        <ButtonControl
            newStyle
            class='refresh-button'
            onClick={this.onRefresh} size='medium'
            title={<div><Icon icon='refresh' height={12} width={12} />Refresh</div>}
        />
        { withColumns && <OverviewColumns
            selectedColumns={columns}
            defaultColumns={model}
            applySelectedColumns={this.applySelectedColumns}
        /> }
      </div>,
      withMultiSelect: !!bulkActions,
      onSelectRow: this.onSelectRow,
      onSelectAll: this.onSelectAll,
      onClearSelection: () => this.setState({ selectedRows: [] }),
      selectedRows,
      allSelected: selectedRows.length === data.length,
      entity: dataKey,
      bulkActions: <div className='overview-bulk-actions'>
        {bulkActions && bulkActions.map((bulkAction) => {
          const { Component } = bulkAction
          return <Component
              numberOfSelectedTopics={selectedRows.length}
              performBulkAction={this.performBulkAction}
          />
        })}
      </div>
    }

    if (withSorting) {
      const { sort } = this.state
      sort && Object.keys(sort).forEach((key: string) => {
        Object.assign(tableProps, { sort: { order: key, direction: sort[key] }, onSortChange: this.onSortChange })
      })
    }

    if (withPagination) {
      Object.assign(tableProps, { withPagination: this.getPaginationProps()})
    }

    return tableProps
  }

  async performBulkAction ({ onClick, successMessage }: BulkAction) {
    this.setState({ loading: true })

    const { data, selectedRows } = this.state
    const selectedObjects: any = data.filter((object) => selectedRows.includes(object[this.props.rowId]))

    await onClick(selectedObjects)
      .then(async () => {
        await this.fetch()
        this.setState({
          showAlert: true,
          alertMessage: successMessage,
          alertType: 'success',
          alertVisibleFor: 3000
        })
    }).catch(() => this.showSomethingWentWrong())
  }

  onSelectRow (rowId: number, checked: boolean) {
    this.setState(
      ({ selectedRows }) => ({
        selectedRows: checked
          ? [...this.state.selectedRows, rowId]
          : selectedRows.filter((selectedRowId: number) => selectedRowId !== rowId) }))
  }

  onSelectAll () {
    this.setState(
      ({ selectedRows, data }) => ({
        selectedRows: selectedRows.length === data.length
          ? []
          : data.map((row: { publicId: number,  id: number}) => row.publicId || row.id) }))
  }

  applySelectedColumns (columns: Model[]) {
    this.setState({ columns: columns })

    if (Overview.isCustomView() || Overview.isSharedView()) {
      return
    }

    let storage = JSON.parse(window.sessionStorage.getItem(this.STORAGE_KEY) as string)
    storage.columns = this.getColumnsForView(columns)
    window.sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(storage))
  }

  validateCustomViewTitleName () {
    const { customViewTitle } = this.state

    if (!customViewTitle) {
      this.setState({
        showAlert: true,
        alertType: 'error',
        alertMessage: 'Please enter a name for your custom view',
        showSaveCustomViewModal: false
      })
      return
    }
  }

  trackCustomViewOpened () {
    tracker.trackEvent('userevent', {
      type: 'Custom View Opened',
      new_overview: true,
      view_type: this.props.withCustomViews,
      parameters: this.getViewParameters()
  })
  }

  trackCustomViewSaved () {
    tracker.trackEvent('userevent', {
      type: 'Custom View Saved',
      view_type: this.props.withCustomViews,
      parameters: this.getViewParameters()
    })
  }

  trackCustomViewShared () {
    tracker.trackEvent('userevent', {
      type: 'Custom View Shared',
      view_type: this.props.withCustomViews,
      parameters: this.getViewParameters()
    })
  }

  render ({
    type,
    headerTitle,
    headerButtons,
    withSearch,
    searchPlaceholder,
    withPagination,
    withFilters,
    rowId,
    model,
    EmptyStateComponent,
    isBeta = false,
    withCustomViews
  }: Props, {
    data,
    total,
    filters,
    columns,
    loading,
    areFiltersApplied,
    sort,
    showAlert,
    alertMessage,
    alertType,
    alertVisibleFor,
    customViewId,
    customViewRadioInput,
    customViewError,
    customViewTitle
  }: State) {
    const isCustomView = !!customViewId
    return <div class='overview'>
      {loading && <Loader />}
      {showAlert && <AlertComponent
        message={alertMessage}
        messageType={alertType}
        isVisibleFor={alertVisibleFor}
        sticky
        onTimeoutEnd={() => this.setState({ showAlert: false })}
      />}
      <Header
        type={type}
        title={ isCustomView ? customViewTitle : headerTitle }
        isCustomView={isCustomView}
        onTitleChanged={(title: string) => this.setState({ customViewTitle: title})}
        customButtons={headerButtons}
        isBeta={isBeta}
        customViewActions={!!withCustomViews ? <CustomViewActions
          isCustomView={isCustomView}
          customViewName={customViewTitle}
          onShareView={this.onShareView}
          onSaveCustomView={this.onSaveCustomView}
          onDeleteCustomView={this.onDeleteCustomView}
          errorMessage={customViewError} /> : undefined
        }
      />
      { withFilters && (data.length > 0 || areFiltersApplied || Overview.isSharedView()) && <OverviewFilters
        filters={withFilters}
        appliedFilters={filters}
        onFilterChange={this.onFilterChange}
        onClearFilterCriteria={this.onClearFilterCriteria}
        withSearch={withSearch}
        searchPlaceholder={searchPlaceholder}
      /> }
      {data.length > 0
        ? <Table {...this.getTableProps()} />
        : (!loading && (areFiltersApplied || Overview.isSharedView()))
          ? <div>
            <EmptyPage
              img_src={`${this.defaultCdn}/images/empty_result.png`}
              header='No results found'
              text={'Sorry, that filter combination has no results.\n Please try different criteria.'}
            />
          </div> : !loading && EmptyStateComponent
      }
    </div>
  }
}

export default Overview
